import { createComponent, type IRenderer } from '@mentimeter/ragnar-react';
import type { CSSProperties } from 'react';
import type React from 'react';

// Entire file copied from: https://github.com/mentimeter/mm-js/blob/cbf356e4e64e5e26382a29dc60a379fec1f86c8c/packages/sfinx/src/renderers/components/Stagger.tsx

type StaggerType = 'fade' | 'brand';

interface StaggerT {
  index: number;
  type?: StaggerType;
}

interface StaggerCSST extends StaggerT {
  children: React.ReactNode;
  duration?: number | undefined;
  delay?: number | undefined;
  width?: string;
  flex?: string;
  maxWidth?: string;
  height?: string;
  enableAnimations?: boolean;
  style?: CSSProperties;
}

const variants: Record<
  StaggerType,
  Parameters<IRenderer['renderKeyframe']>[0]
> = {
  fade: () => ({
    // IE11 jumping animation fix
    '0.01%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  }),
  brand: () => ({
    // IE11 jumping animation fix
    '0.01%': {
      transform: `translateY(50px)`,
      opacity: 0,
    },
    '100%': {
      transform: `translateY(0)`,
      opacity: 1,
    },
  }),
};

const createCssStagger = () => {
  const rule = (
    {
      type = 'brand',
      index = 0,
      duration = 400,
      delay = 250,
      flex = 'inherit',
      width = '100%',
      height = '100%',
      maxWidth = '100%',
      enableAnimations = false,
    }: StaggerCSST,
    renderer: IRenderer,
  ) => {
    const keyFrame = variants[type];
    const animation = renderer.renderKeyframe(keyFrame, {});
    return {
      opacity: enableAnimations ? 0 : 1,
      animationName: enableAnimations ? animation : 'none',
      animationDuration: `${duration}ms`,
      animationTimingFunction: 'ease',
      animationFillMode: 'forwards',
      animationDelay: `${index * delay}ms`,
      display: 'inherit',
      flex,
      flexDirection: 'inherit',
      alignItems: 'inherit',
      justifyContent: 'inherit',
      textAlign: 'inherit',
      font: 'inherit',
      width,
      height,
      maxWidth,
      minHeight: 'inherit',
      minWidth: 'inherit',
    };
  };
  return createComponent(rule, 'div');
};

export const StaggerCss: React.ComponentType<StaggerCSST> = createCssStagger();
